import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Unpublished as UnpublishedIcon,
} from '@mui/icons-material';
import { useNavigation, useDelete } from '@refinedev/core';
import { useTranslation } from 'react-i18next';
import { useDuplicateStudy } from '../hooks/useDuplicateStudy';
import { useUnpublishStudy } from '../hooks/useUnpublishStudy';
import { UnpublishConfirmationModal } from './UnpublishConfirmationModal';

type StudyActionsDropdownProps = {
  studyId: string;
  studyName: string;
  studyStatus: 'draft' | 'published' | 'finished';
  canEdit: boolean;
  canDelete: boolean;
};

export const StudyActionsDropdown: React.FC<StudyActionsDropdownProps> = ({
  studyId,
  studyName,
  studyStatus,
  canEdit,
  canDelete,
}) => {
  const { t } = useTranslation();
  const { show, edit } = useNavigation();
  const { mutate: deleteStudy } = useDelete();
  const { duplicateStudy, isLoading: isDuplicating } = useDuplicateStudy();
  const { unpublishStudy, isLoading: isUnpublishing } = useUnpublishStudy();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleShow = () => {
    show('admin/studies', studyId);
    handleClose();
  };

  const handleEdit = () => {
    edit('admin/studies', studyId);
    handleClose();
  };

  const handleDelete = () => {
    if (window.confirm(t('dialogs.confirmDelete', { name: studyName }))) {
      deleteStudy({
        resource: 'admin/studies',
        id: studyId,
      });
    }
    handleClose();
  };

  const handleDuplicate = () => {
    duplicateStudy(studyId);
    handleClose();
  };

  const handleUnpublish = () => {
    setUnpublishModalOpen(true);
    handleClose();
  };

  const handleConfirmUnpublish = () => {
    unpublishStudy(studyId);
    setUnpublishModalOpen(false);
  };

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleShow}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('buttons.show')}</ListItemText>
        </MenuItem>

        {studyStatus === 'draft' && (
          <MenuItem onClick={handleEdit} disabled={!canEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('buttons.edit')}</ListItemText>
          </MenuItem>
        )}
        {studyStatus !== 'draft' && (
          <MenuItem onClick={handleUnpublish}>
            <ListItemIcon>
              <UnpublishedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('pages.studies.actions.unpublish')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} disabled={!canDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('buttons.delete')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDuplicate} disabled={isDuplicating}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('buttons.duplicate')}</ListItemText>
        </MenuItem>
      </Menu>
      <UnpublishConfirmationModal
        open={unpublishModalOpen}
        onClose={() => setUnpublishModalOpen(false)}
        onConfirm={handleConfirmUnpublish}
        isLoading={isUnpublishing}
      />
    </>
  );
};
